import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type {
  CheckState,
  DbMetadata,
  DefectInstance,
  Property,
  Room,
  User,
} from './types';

/**
 * IndexedDB schema for propdefect.
 *
 * Versioning: bump `DB_VERSION` AND add a migration block in the
 * `upgrade()` callback below. NEVER delete or rename a previous
 * version's migration block — old browsers may still be at that
 * version. Each migration block runs forward-only from its `from`
 * version to its `to` version.
 *
 * Indexes are scoped to userId / propertyId / roomId so per-user
 * isolation is enforced at the storage layer, not just the repository.
 */

const DB_NAME = 'propdefect';
const DB_VERSION = 1;

interface PropdefectDB extends DBSchema {
  users: {
    key: string;
    value: User;
    indexes: {
      'by-email': string;
    };
  };
  properties: {
    key: string;
    value: Property;
    indexes: {
      'by-user': string;
    };
  };
  rooms: {
    key: string;
    value: Room;
    indexes: {
      'by-property': string;
      'by-user': string;
    };
  };
  checkStates: {
    key: string;
    value: CheckState;
    indexes: {
      'by-room': string;
      'by-property': string;
      'by-user': string;
    };
  };
  defectInstances: {
    key: string;
    value: DefectInstance;
    indexes: {
      'by-check-state': string;
      'by-user': string;
    };
  };
  metadata: {
    key: 'global';
    value: DbMetadata;
  };
}

let dbPromise: Promise<IDBPDatabase<PropdefectDB>> | null = null;

/** Open (or reuse) the IndexedDB connection. Idempotent. */
export function getDb(): Promise<IDBPDatabase<PropdefectDB>> {
  if (dbPromise) return dbPromise;
  dbPromise = openDB<PropdefectDB>(DB_NAME, DB_VERSION, {
    upgrade(db, oldVersion) {
      // v0 → v1: initial schema.
      if (oldVersion < 1) {
        const users = db.createObjectStore('users', { keyPath: 'id' });
        users.createIndex('by-email', 'email', { unique: false });

        const properties = db.createObjectStore('properties', {
          keyPath: 'id',
        });
        properties.createIndex('by-user', 'userId', { unique: false });

        const rooms = db.createObjectStore('rooms', { keyPath: 'id' });
        rooms.createIndex('by-property', 'propertyId', { unique: false });
        rooms.createIndex('by-user', 'userId', { unique: false });

        const checkStates = db.createObjectStore('checkStates', {
          keyPath: 'id',
        });
        checkStates.createIndex('by-room', 'roomId', { unique: false });
        checkStates.createIndex('by-property', 'propertyId', { unique: false });
        checkStates.createIndex('by-user', 'userId', { unique: false });

        const defectInstances = db.createObjectStore('defectInstances', {
          keyPath: 'id',
        });
        defectInstances.createIndex('by-check-state', 'checkStateId', {
          unique: false,
        });
        defectInstances.createIndex('by-user', 'userId', { unique: false });

        db.createObjectStore('metadata', { keyPath: 'id' });
      }
      // Future versions: add `if (oldVersion < 2) { ... }` blocks here.
    },
    blocked() {
      // A different tab is holding an older version open. Refuse to
      // upgrade silently — surface this to the caller so the user is
      // told to close other tabs.
      throw new Error(
        'IndexedDB upgrade blocked — close other tabs of this app and retry.',
      );
    },
    terminated() {
      // The connection was closed unexpectedly. Drop the cached
      // promise so the next call reopens cleanly.
      dbPromise = null;
    },
  });
  return dbPromise;
}

/** Close the connection (test cleanup; rarely useful in production). */
export async function closeDb(): Promise<void> {
  if (!dbPromise) return;
  const db = await dbPromise;
  db.close();
  dbPromise = null;
}

/**
 * Reset (delete the database). Destructive — for tests + the "Delete
 * my account" path in step 5.9.
 */
export async function deleteDb(): Promise<void> {
  await closeDb();
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase(DB_NAME);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
    req.onblocked = () =>
      reject(
        new Error(
          'Cannot delete IndexedDB — another tab still holds a connection.',
        ),
      );
  });
}

/** Current schema version constant — exposed for the metadata record + tests. */
export const CURRENT_SCHEMA_VERSION = DB_VERSION;
